import { useState, useRef, useEffect } from 'react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, addMonths, subMonths, startOfWeek, endOfWeek, setMonth, setYear } from 'date-fns';
import { fr } from 'date-fns/locale';

type PickerVariant = 'datetime' | 'monthYear';

interface DateTimePickerProps {
  value: string; // ISO datetime (YYYY-MM-DDTHH:mm) or month-year (YYYY-MM)
  onChange: (value: string) => void;
  label?: string;
  /** 'datetime' = date + heure, 'monthYear' = mois + année uniquement */
  variant?: PickerVariant;
}

const MONTHS = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
const CURRENT_YEAR = new Date().getFullYear();
const YEAR_RANGE = 20;
const YEARS = Array.from({ length: YEAR_RANGE * 2 + 1 }, (_, i) => CURRENT_YEAR - YEAR_RANGE + i);

export function DateTimePicker({ value, onChange, label, variant = 'datetime' }: DateTimePickerProps) {
  const isMonthYear = variant === 'monthYear';

  const parseValue = (): Date => {
    if (!value) return new Date();
    if (isMonthYear && /^\d{4}-\d{2}$/.test(value)) {
      return new Date(value + '-01T00:00:00');
    }
    return new Date(value);
  };

  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(parseValue);
  const [selectedDate, setSelectedDate] = useState<Date | null>(value ? parseValue() : null);
  const [timeValue, setTimeValue] = useState(() => {
    if (value && !isMonthYear) {
      const d = new Date(value);
      return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    }
    return '00:00';
  });

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (value) {
      const d = parseValue();
      setCurrentMonth(d);
      setSelectedDate(d);
    }
  }, [value, isMonthYear]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const handleDateSelect = (date: Date) => {
    setSelectedDate(date);
    if (isMonthYear) {
      onChange(format(date, 'yyyy-MM'));
      return;
    }
    if (timeValue) {
      const [hours, minutes] = timeValue.split(':').map(Number);
      const d = new Date(date);
      d.setHours(hours, minutes, 0, 0);
      onChange(format(d, "yyyy-MM-dd'T'HH:mm"));
    }
  };

  const handleMonthYearConfirm = () => {
    onChange(format(currentMonth, 'yyyy-MM'));
    setSelectedDate(startOfMonth(currentMonth));
    setIsOpen(false);
  };

  const handleTimeChange = (time: string) => {
    setTimeValue(time);
    if (selectedDate) {
      const [hours, minutes] = time.split(':').map(Number);
      const newDate = new Date(selectedDate);
      newDate.setHours(hours, minutes, 0, 0);
      onChange(format(newDate, "yyyy-MM-dd'T'HH:mm"));
    }
  };

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 });
  const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
  const days = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

  const weekDays = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

  const displayValue = value
    ? isMonthYear
      ? format(parseValue(), 'MM/yyyy')
      : format(new Date(value), "dd/MM/yyyy HH:mm")
    : isMonthYear
      ? 'Sélectionner un mois'
      : 'Sélectionner une date';

  return (
    <div className="relative" ref={containerRef}>
      {label && <label className="text-sm font-semibold text-slate-600 dark:text-slate-300 mb-1 block">{label}</label>}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg px-3 py-2 text-slate-800 dark:text-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary flex items-center justify-between hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
      >
        <span className="flex items-center gap-2">
          <span className="material-icons-outlined text-slate-500 dark:text-slate-400" style={{ fontSize: '18px' }}>calendar_today</span>
          {displayValue}
        </span>
        <span className="material-icons-outlined text-slate-500 dark:text-slate-400" style={{ fontSize: '18px' }}>
          {isOpen ? 'expand_less' : 'expand_more'}
        </span>
      </button>

      {isOpen && (
        <div className="absolute z-50 mt-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-2xl p-4 min-w-[320px]">
          {isMonthYear ? (
            <>
              {/* Mois / Année uniquement */}
              <div className="flex items-center justify-between gap-2 mb-4">
                <button
                  type="button"
                  onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
                  className="p-2 rounded-lg text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                >
                  <span className="material-icons-outlined" style={{ fontSize: '20px' }}>chevron_left</span>
                </button>
                <div className="flex gap-2 flex-1">
                  <select
                    value={currentMonth.getMonth()}
                    onChange={(e) => setCurrentMonth(setMonth(currentMonth, Number(e.target.value)))}
                    className="flex-1 bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-lg px-2 py-2 text-slate-800 dark:text-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary capitalize"
                  >
                    {MONTHS.map((m) => (
                      <option key={m} value={m}>
                        {format(new Date(2000, m, 1), 'MMMM', { locale: fr })}
                      </option>
                    ))}
                  </select>
                  <select
                    value={currentMonth.getFullYear()}
                    onChange={(e) => setCurrentMonth(setYear(currentMonth, Number(e.target.value)))}
                    className="flex-1 min-w-[80px] bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-lg px-2 py-2 text-slate-800 dark:text-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    {YEARS.map((y) => (
                      <option key={y} value={y}>
                        {y}
                      </option>
                    ))}
                  </select>
                </div>
                <button
                  type="button"
                  onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
                  className="p-2 rounded-lg text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                >
                  <span className="material-icons-outlined" style={{ fontSize: '20px' }}>chevron_right</span>
                </button>
              </div>
              <div className="flex gap-2 pt-2 border-t border-slate-200 dark:border-slate-700">
                <button
                  type="button"
                  onClick={handleMonthYearConfirm}
                  className="flex-1 px-3 py-2 bg-primary/20 border border-primary/30 text-primary rounded-lg text-sm font-semibold hover:bg-primary/30 transition-colors"
                >
                  Valider
                </button>
              </div>
            </>
          ) : (
            <>
              {/* Month Navigation */}
              <div className="flex items-center justify-between mb-4">
                <button
                  type="button"
                  onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
                  className="p-2 rounded-lg text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                >
                  <span className="material-icons-outlined" style={{ fontSize: '20px' }}>chevron_left</span>
                </button>
                <h3 className="text-lg font-bold text-slate-900 dark:text-white capitalize">
                  {format(currentMonth, 'MMMM yyyy', { locale: fr })}
                </h3>
                <button
                  type="button"
                  onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
                  className="p-2 rounded-lg text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                >
                  <span className="material-icons-outlined" style={{ fontSize: '20px' }}>chevron_right</span>
                </button>
              </div>

              {/* Week Days Header */}
              <div className="grid grid-cols-7 gap-1 mb-2">
                {weekDays.map((day) => (
                  <div key={day} className="text-center text-xs font-semibold text-slate-500 dark:text-slate-400 py-1">
                    {day}
                  </div>
                ))}
              </div>

              {/* Calendar Days */}
              <div className="grid grid-cols-7 gap-1 mb-4">
                {days.map((day) => {
                  const isCurrentMonthDay = isSameMonth(day, currentMonth);
                  const isSelected = selectedDate && isSameDay(day, selectedDate);
                  const isToday = isSameDay(day, new Date());

                  return (
                    <button
                      key={day.toISOString()}
                      type="button"
                      onClick={() => handleDateSelect(day)}
                      className={`
                        aspect-square rounded-lg text-sm font-semibold transition-colors
                        ${isCurrentMonthDay ? 'text-slate-800 dark:text-slate-200' : 'text-slate-400 dark:text-slate-600'}
                        ${isSelected
                          ? 'bg-primary text-white border-2 border-primary'
                          : isToday
                            ? 'bg-slate-200 dark:bg-slate-700 text-slate-900 dark:text-white border-2 border-slate-300 dark:border-slate-600'
                            : 'hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-800 dark:text-slate-200'
                        }
                      `}
                    >
                      {format(day, 'd')}
                    </button>
                  );
                })}
              </div>

              {/* Time Picker */}
              <div className="flex items-center gap-3 pt-4 border-t border-slate-200 dark:border-slate-700">
                <label className="text-sm font-semibold text-slate-600 dark:text-slate-300 whitespace-nowrap">Heure :</label>
                <input
                  type="time"
                  value={timeValue}
                  onChange={(e) => handleTimeChange(e.target.value)}
                  className="flex-1 bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-lg px-3 py-2 text-slate-800 dark:text-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              {/* Quick Actions */}
              <div className="flex gap-2 mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
                <button
                  type="button"
                  onClick={() => {
                    const now = new Date();
                    setSelectedDate(now);
                    setCurrentMonth(now);
                    setTimeValue(format(now, 'HH:mm'));
                    onChange(format(now, "yyyy-MM-dd'T'HH:mm"));
                  }}
                  className="flex-1 px-3 py-2 bg-slate-100 dark:bg-slate-700 border border-slate-300 dark:border-slate-600 text-slate-800 dark:text-slate-200 rounded-lg text-sm font-semibold hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
                >
                  Maintenant
                </button>
                <button
                  type="button"
                  onClick={() => setIsOpen(false)}
                  className="flex-1 px-3 py-2 bg-primary/20 border border-primary/30 text-primary rounded-lg text-sm font-semibold hover:bg-primary/30 transition-colors"
                >
                  Valider
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
